/* eslint-disable react/prop-types */
import React from 'react';
import {
    Grid,
    TextField,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    FormHelperText,
    OutlinedInput,
    ListItemText,
    Checkbox,
    Box,
    FormLabel,
    Typography,
    Collapse,
    IconButton,
    RadioGroup,
    FormControlLabel,
    Radio,
    Autocomplete,
    Chip
} from '@mui/material';
import { Field, ErrorMessage } from 'formik';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import SubCard from 'ui-component/cards/SubCard';
import { gridSpacing } from 'store/constant';
import {
    accessibilityFeatures,
    appliances,
    appliancesMaintained,
    communityFeatures,
    exteriorFeatures,
    fencing,
    fireplaceFeatures,
    furnished,
    guestAccommodations,
    horseAmenities,
    interiorFeatures,
    laundryLocation,
    otherStructures,
    patioAndPorchFeatures,
    privatePoolFeatures,
    securityFeatures,
    spaFeatures,
    windowFeatures
} from './data/additional_data';
import HelpIcon from './HelpIcon';
import { tips } from './HelpData';
import { flooring } from './data/general_data';

const distances = ['Less Than 1 Mile', '1-2 Miles', '2+ Miles', 'In Subdivision', 'See Remarks'];

const AdditionalInfo = ({ errors, handleChange, handleBlur, values, touched, setFieldValue }) => {
    const [openOptional, setOpenOptional] = React.useState(true);

    const handleToggle = () => {
        setOpenOptional((prev) => !prev);
    };

    return (
        <Grid item xs={12}>
            <Grid container spacing={gridSpacing}>
                <Grid item xs={12}>
                    <Grid item xs={12} sx={{ mt: 2 }}>
                        <Typography
                            sx={{
                                color: '#919191',
                                fontSize: '16px',
                                fontStyle: 'normal',
                                fontWeight: '700',
                                lineHeight: 'normal',
                                textTransform: 'capitalize',
                                mb: 2
                            }}
                        >
                            Additional Information
                        </Typography>
                        <SubCard>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={4}>
                                    <FormControl fullWidth required error={touched.interiorFeatures && Boolean(errors.interiorFeatures)}>
                                        <Box sx={{ display: 'flex', alignItems: 'start' }}>
                                            <FormLabel sx={{ mb: 1, '& .MuiFormLabel-asterisk': { color: 'red' } }}>
                                                <Typography variant="subtitle1" fontWeight="bold" component="span">
                                                    Interior Features
                                                </Typography>
                                            </FormLabel>
                                            <HelpIcon
                                                title={tips?.InteriorFeatures.title}
                                                ruleHelp={tips?.InteriorFeatures.ruleHelp}
                                                generalHelpDesc={tips?.InteriorFeatures.generalHelpDesc}
                                                generalHelp={tips?.InteriorFeatures.generalHelp}
                                            />
                                        </Box>

                                        <Autocomplete
                                            renderTags={(tagValue, getTagProps) =>
                                                tagValue.map((option, index) => (
                                                    <Chip
                                                        key={index}
                                                        label={option}
                                                        {...getTagProps({ index })}
                                                        sx={{
                                                            '& .MuiChip-deleteIcon': {
                                                                color: 'black !important'
                                                            }
                                                        }}
                                                    />
                                                ))
                                            }
                                            multiple
                                            options={interiorFeatures} // Assuming interiorFeatures is an array of options
                                            value={values.interiorFeatures}
                                            onChange={(event, newValue) => setFieldValue('interiorFeatures', newValue)}
                                            onBlur={handleBlur}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    error={Boolean(touched.interiorFeatures && errors.interiorFeatures)}
                                                />
                                            )}
                                            disableCloseOnSelect
                                            renderOption={(props, option, { selected }) => (
                                                <li {...props}>
                                                    <Checkbox checked={selected} />
                                                    {option}
                                                </li>
                                            )}
                                        />

                                        {touched.interiorFeatures && errors.interiorFeatures && (
                                            <FormHelperText>{errors.interiorFeatures}</FormHelperText>
                                        )}
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <FormControl fullWidth required error={touched.exteriorFeatures && Boolean(errors.exteriorFeatures)}>
                                        <Box sx={{ display: 'flex', alignItems: 'start' }}>
                                            <FormLabel sx={{ mb: 1, '& .MuiFormLabel-asterisk': { color: 'red' } }}>
                                                <Typography variant="subtitle1" fontWeight="bold" component="span">
                                                    Exterior Features
                                                </Typography>
                                            </FormLabel>
                                            <HelpIcon
                                                title={tips?.ExteriorFeatures.title}
                                                ruleHelp={tips?.ExteriorFeatures.ruleHelp}
                                                generalHelpDesc={tips?.ExteriorFeatures.generalHelpDesc}
                                            />
                                        </Box>

                                        <Autocomplete
                                            renderTags={(tagValue, getTagProps) =>
                                                tagValue.map((option, index) => (
                                                    <Chip
                                                        key={index}
                                                        label={option}
                                                        {...getTagProps({ index })}
                                                        sx={{
                                                            '& .MuiChip-deleteIcon': {
                                                                color: 'black !important'
                                                            }
                                                        }}
                                                    />
                                                ))
                                            }
                                            multiple
                                            options={exteriorFeatures}
                                            value={values.exteriorFeatures}
                                            onChange={(event, newValue) => setFieldValue('exteriorFeatures', newValue)}
                                            onBlur={handleBlur}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    error={Boolean(touched.exteriorFeatures && errors.exteriorFeatures)}
                                                />
                                            )}
                                            disableCloseOnSelect
                                            renderOption={(props, option, { selected }) => (
                                                <li {...props}>
                                                    <Checkbox checked={selected} />
                                                    {option}
                                                </li>
                                            )}
                                        />

                                        {touched.exteriorFeatures && errors.exteriorFeatures && (
                                            <FormHelperText>{errors.exteriorFeatures}</FormHelperText>
                                        )}
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <FormControl fullWidth required error={touched.appliances && Boolean(errors.appliances)}>
                                        <Box sx={{ display: 'flex', alignItems: 'start' }}>
                                            <FormLabel sx={{ mb: 1, '& .MuiFormLabel-asterisk': { color: 'red' } }}>
                                                <Typography variant="subtitle1" fontWeight="bold" component="span">
                                                    Appliances
                                                </Typography>
                                            </FormLabel>
                                            <HelpIcon
                                                title={tips?.Appliances.title}
                                                ruleHelp={tips?.Appliances.ruleHelp}
                                                generalHelpDesc={tips?.Appliances.generalHelpDesc}
                                            />
                                        </Box>
                                        <Autocomplete
                                            renderTags={(tagValue, getTagProps) =>
                                                tagValue.map((option, index) => (
                                                    <Chip
                                                        key={index}
                                                        label={option}
                                                        {...getTagProps({ index })}
                                                        sx={{
                                                            '& .MuiChip-deleteIcon': {
                                                                color: 'black !important'
                                                            }
                                                        }}
                                                    />
                                                ))
                                            }
                                            multiple
                                            options={appliances}
                                            value={values.appliances || []}
                                            onChange={(event, newValue) => setFieldValue('appliances', newValue)}
                                            onBlur={handleBlur}
                                            renderInput={(params) => (
                                                <TextField {...params} error={Boolean(touched.appliances && errors.appliances)} />
                                            )}
                                            disableCloseOnSelect
                                            renderOption={(props, option, { selected }) => (
                                                <li {...props}>
                                                    <Checkbox checked={selected} />
                                                    {option}
                                                </li>
                                            )}
                                        />
                                        {touched.appliances && errors.appliances && <FormHelperText>{errors.appliances}</FormHelperText>}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <FormControl fullWidth required error={touched.securityFeatures && Boolean(errors.securityFeatures)}>
                                        <Box sx={{ display: 'flex', alignItems: 'start' }}>
                                            <FormLabel sx={{ mb: 1, '& .MuiFormLabel-asterisk': { color: 'red' } }}>
                                                <Typography variant="subtitle1" fontWeight="bold" component="span">
                                                    Security Features
                                                </Typography>
                                            </FormLabel>
                                            <HelpIcon
                                                title={tips?.SecurityFeatures.title}
                                                ruleHelp={tips?.SecurityFeatures.ruleHelp}
                                                generalHelpDesc={tips?.SecurityFeatures.generalHelpDesc}
                                            />
                                        </Box>
                                        <Autocomplete
                                            renderTags={(tagValue, getTagProps) =>
                                                tagValue.map((option, index) => (
                                                    <Chip
                                                        key={index}
                                                        label={option}
                                                        {...getTagProps({ index })}
                                                        sx={{
                                                            '& .MuiChip-deleteIcon': {
                                                                color: 'black !important'
                                                            }
                                                        }}
                                                    />
                                                ))
                                            }
                                            multiple
                                            options={securityFeatures}
                                            value={values.securityFeatures || []}
                                            onChange={(event, newValue) => setFieldValue('securityFeatures', newValue)}
                                            onBlur={handleBlur}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    error={Boolean(touched.securityFeatures && errors.securityFeatures)}
                                                />
                                            )}
                                            disableCloseOnSelect
                                            renderOption={(props, option, { selected }) => (
                                                <li {...props}>
                                                    <Checkbox checked={selected} />
                                                    {option}
                                                </li>
                                            )}
                                        />
                                        {touched.securityFeatures && errors.securityFeatures && (
                                            <FormHelperText>{errors.securityFeatures}</FormHelperText>
                                        )}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <FormControl
                                        fullWidth
                                        required
                                        error={touched.accessibilityFeatures && Boolean(errors.accessibilityFeatures)}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'start' }}>
                                            <FormLabel sx={{ mb: 1, '& .MuiFormLabel-asterisk': { color: 'red' } }}>
                                                <Typography variant="subtitle1" fontWeight="bold" component="span">
                                                    Accessibility Features
                                                </Typography>
                                            </FormLabel>
                                            <HelpIcon
                                                title={tips?.AccessibilityFeatures.title}
                                                ruleHelp={tips?.AccessibilityFeatures.ruleHelp}
                                                generalHelpDesc={tips?.AccessibilityFeatures.generalHelpDesc}
                                            />
                                        </Box>
                                        <Autocomplete
                                            renderTags={(tagValue, getTagProps) =>
                                                tagValue.map((option, index) => (
                                                    <Chip
                                                        key={index}
                                                        label={option}
                                                        {...getTagProps({ index })}
                                                        sx={{
                                                            '& .MuiChip-deleteIcon': {
                                                                color: 'black !important'
                                                            }
                                                        }}
                                                    />
                                                ))
                                            }
                                            multiple
                                            options={accessibilityFeatures}
                                            value={values.accessibilityFeatures || []}
                                            onChange={(event, newValue) => setFieldValue('accessibilityFeatures', newValue)}
                                            onBlur={handleBlur}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    error={Boolean(touched.accessibilityFeatures && errors.accessibilityFeatures)}
                                                />
                                            )}
                                            disableCloseOnSelect
                                            renderOption={(props, option, { selected }) => (
                                                <li {...props}>
                                                    <Checkbox checked={selected} />
                                                    {option}
                                                </li>
                                            )}
                                        />
                                        {touched.accessibilityFeatures && errors.accessibilityFeatures && (
                                            <FormHelperText>{errors.accessibilityFeatures}</FormHelperText>
                                        )}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <FormControl
                                        fullWidth
                                        required
                                        error={touched.privatePoolFeatures && Boolean(errors.privatePoolFeatures)}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'start' }}>
                                            <FormLabel sx={{ mb: 1, '& .MuiFormLabel-asterisk': { color: 'red' } }}>
                                                <Typography variant="subtitle1" fontWeight="bold" component="span">
                                                    Private Pool Features (On Property)
                                                </Typography>
                                            </FormLabel>
                                            <HelpIcon
                                                title={tips?.PoolFeatures.title}
                                                ruleHelp={tips?.PoolFeatures.ruleHelp}
                                                generalHelpDesc={tips?.PoolFeatures.generalHelpDesc}
                                            />
                                        </Box>
                                        <Autocomplete
                                            renderTags={(tagValue, getTagProps) =>
                                                tagValue.map((option, index) => (
                                                    <Chip
                                                        key={index}
                                                        label={option}
                                                        {...getTagProps({ index })}
                                                        sx={{
                                                            '& .MuiChip-deleteIcon': {
                                                                color: 'black !important'
                                                            }
                                                        }}
                                                    />
                                                ))
                                            }
                                            multiple
                                            options={privatePoolFeatures}
                                            value={values.privatePoolFeatures || []}
                                            onChange={(event, newValue) => setFieldValue('privatePoolFeatures', newValue)}
                                            onBlur={handleBlur}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    error={Boolean(touched.privatePoolFeatures && errors.privatePoolFeatures)}
                                                />
                                            )}
                                            disableCloseOnSelect
                                            renderOption={(props, option, { selected }) => (
                                                <li {...props}>
                                                    <Checkbox checked={selected} />
                                                    {option}
                                                </li>
                                            )}
                                        />
                                        {touched.privatePoolFeatures && errors.privatePoolFeatures && (
                                            <FormHelperText>{errors.privatePoolFeatures}</FormHelperText>
                                        )}
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <FormControl fullWidth required error={touched.flooring && Boolean(errors.flooring)}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <FormLabel sx={{ mb: 1, '& .MuiFormLabel-asterisk': { color: 'red' } }}>
                                                <Typography variant="subtitle1" fontWeight="bold" component="span">
                                                    Flooring Details
                                                </Typography>
                                            </FormLabel>
                                            <HelpIcon
                                                title={tips?.flooring.title}
                                                ruleHelp={tips?.flooring.ruleHelp}
                                                generalHelpDesc={tips?.flooring.generalHelpDesc}
                                                generalHelp={tips?.flooring.generalHelp}
                                            />
                                        </Box>
                                        <Autocomplete
                                            renderTags={(tagValue, getTagProps) =>
                                                tagValue.map((option, index) => (
                                                    <Chip
                                                        key={index}
                                                        label={option}
                                                        {...getTagProps({ index })}
                                                        sx={{
                                                            '& .MuiChip-deleteIcon': {
                                                                color: 'black !important'
                                                            }
                                                        }}
                                                    />
                                                ))
                                            }
                                            multiple
                                            options={flooring}
                                            value={values.flooring || []}
                                            onChange={(event, newValue) => setFieldValue('flooring', newValue)}
                                            onBlur={handleBlur}
                                            renderInput={(params) => (
                                                <TextField {...params} error={Boolean(touched.flooring && errors.flooring)} />
                                            )}
                                            disableCloseOnSelect
                                            renderOption={(props, option, { selected }) => (
                                                <li {...props}>
                                                    <Checkbox checked={selected} />
                                                    {option}
                                                </li>
                                            )}
                                        />
                                        {touched.flooring && errors.flooring && <FormHelperText>{errors.flooring}</FormHelperText>}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <FormControl fullWidth required error={touched.communityFeatures && Boolean(errors.communityFeatures)}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <FormLabel sx={{ mb: 1, '& .MuiFormLabel-asterisk': { color: 'red' } }}>
                                                <Typography variant="subtitle1" fontWeight="bold" component="span">
                                                    Community Features
                                                </Typography>
                                            </FormLabel>
                                            <HelpIcon
                                                title={tips?.communityFeatures.title}
                                                ruleHelp={tips?.communityFeatures.ruleHelp}
                                                generalHelpDesc={tips?.communityFeatures.generalHelpDesc}
                                                generalHelp={tips?.communityFeatures.generalHelp}
                                            />
                                        </Box>
                                        <Autocomplete
                                            renderTags={(tagValue, getTagProps) =>
                                                tagValue.map((option, index) => (
                                                    <Chip
                                                        key={index}
                                                        label={option}
                                                        {...getTagProps({ index })}
                                                        sx={{
                                                            '& .MuiChip-deleteIcon': {
                                                                color: 'black !important'
                                                            }
                                                        }}
                                                    />
                                                ))
                                            }
                                            multiple
                                            options={communityFeatures}
                                            value={values.communityFeatures || []}
                                            onChange={(event, newValue) => setFieldValue('communityFeatures', newValue)}
                                            onBlur={handleBlur}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    error={Boolean(touched.communityFeatures && errors.communityFeatures)}
                                                />
                                            )}
                                            disableCloseOnSelect
                                            renderOption={(props, option, { selected }) => (
                                                <li {...props}>
                                                    <Checkbox checked={selected} />
                                                    {option}
                                                </li>
                                            )}
                                        />
                                        {touched.communityFeatures && errors.communityFeatures && (
                                            <FormHelperText>{errors.communityFeatures}</FormHelperText>
                                        )}
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <FormControl fullWidth required error={touched.laundryLocation && Boolean(errors.laundryLocation)}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <FormLabel sx={{ mb: 1, '& .MuiFormLabel-asterisk': { color: 'red' } }}>
                                                <Typography variant="subtitle1" fontWeight="bold" component="span">
                                                    Laundry Location
                                                </Typography>
                                            </FormLabel>
                                            <HelpIcon
                                                title={tips?.LaundryLocation.title}
                                                ruleHelp={tips?.LaundryLocation.ruleHelp}
                                                generalHelpDesc={tips?.LaundryLocation.generalHelpDesc}
                                                generalHelp={tips?.LaundryLocation.generalHelp}
                                            />
                                        </Box>
                                        <Autocomplete
                                            renderTags={(tagValue, getTagProps) =>
                                                tagValue.map((option, index) => (
                                                    <Chip
                                                        key={index}
                                                        label={option}
                                                        {...getTagProps({ index })}
                                                        sx={{
                                                            '& .MuiChip-deleteIcon': {
                                                                color: 'black !important'
                                                            }
                                                        }}
                                                    />
                                                ))
                                            }
                                            multiple
                                            options={laundryLocation}
                                            value={values.laundryLocation || []}
                                            onChange={(event, newValue) => setFieldValue('laundryLocation', newValue)}
                                            onBlur={handleBlur}
                                            renderInput={(params) => (
                                                <TextField {...params} error={Boolean(touched.laundryLocation && errors.laundryLocation)} />
                                            )}
                                            disableCloseOnSelect
                                            renderOption={(props, option, { selected }) => (
                                                <li {...props}>
                                                    <Checkbox checked={selected} />
                                                    {option}
                                                </li>
                                            )}
                                        />
                                        {touched.laundryLocation && errors.laundryLocation && (
                                            <FormHelperText>{errors.laundryLocation}</FormHelperText>
                                        )}
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </SubCard>
                    </Grid>

                    <Grid item xs={12} sx={{ mt: 2 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                cursor: 'pointer',
                                mb: 2,
                                p: 1,
                                background: 'white'
                            }}
                            onClick={handleToggle}
                        >
                            <Typography
                                sx={{
                                    // color: '#919191',
                                    fontSize: '16px',
                                    fontWeight: '700',
                                    textTransform: 'capitalize'
                                }}
                            >
                                Optional Information
                            </Typography>
                            <IconButton>{openOptional ? <ExpandLess /> : <ExpandMore />}</IconButton>
                        </Box>

                        <Collapse in={openOptional}>
                            <SubCard sx={{ pt: '20px' }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={4}>
                                        <FormControl fullWidth error={touched.windowFeatures && Boolean(errors.windowFeatures)}>
                                            <Box sx={{ display: 'flex', alignItems: 'start' }}>
                                                <FormLabel sx={{ mb: 1, '& .MuiFormLabel-asterisk': { color: 'red' } }}>
                                                    <Typography variant="subtitle1" fontWeight="bold" component="span">
                                                        Window Features
                                                    </Typography>
                                                </FormLabel>
                                                <HelpIcon
                                                    title={tips?.WindowFeatures.title}
                                                    ruleHelp={tips?.WindowFeatures.ruleHelp}
                                                    generalHelpDesc={tips?.WindowFeatures.generalHelpDesc}
                                                />
                                            </Box>
                                            <Autocomplete
                                                renderTags={(tagValue, getTagProps) =>
                                                    tagValue.map((option, index) => (
                                                        <Chip
                                                            key={index}
                                                            label={option}
                                                            {...getTagProps({ index })}
                                                            sx={{
                                                                '& .MuiChip-deleteIcon': {
                                                                    color: 'black !important'
                                                                }
                                                            }}
                                                        />
                                                    ))
                                                }
                                                multiple
                                                options={windowFeatures}
                                                value={values.windowFeatures || []}
                                                onChange={(event, newValue) => setFieldValue('windowFeatures', newValue)}
                                                onBlur={handleBlur}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        error={Boolean(touched.windowFeatures && errors.windowFeatures)}
                                                    />
                                                )}
                                                disableCloseOnSelect
                                                renderOption={(props, option, { selected }) => (
                                                    <li {...props}>
                                                        <Checkbox checked={selected} />
                                                        {option}
                                                    </li>
                                                )}
                                            />
                                            {touched.windowFeatures && errors.windowFeatures && (
                                                <FormHelperText>{errors.windowFeatures}</FormHelperText>
                                            )}
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sm={4}>
                                        <FormControl
                                            fullWidth
                                            error={touched.patioAndPorchFeatures && Boolean(errors.patioAndPorchFeatures)}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <FormLabel sx={{ mb: 1, '& .MuiFormLabel-asterisk': { color: 'red' } }}>
                                                    <Typography variant="subtitle1" fontWeight="bold" component="span">
                                                        Patio and Porch Features
                                                    </Typography>
                                                </FormLabel>
                                                <HelpIcon
                                                    title={tips?.PatioAndPorchFeatures.title}
                                                    ruleHelp={tips?.PatioAndPorchFeatures.ruleHelp}
                                                    generalHelpDesc={tips?.PatioAndPorchFeatures.generalHelpDesc}
                                                    generalHelp={tips?.PatioAndPorchFeatures.generalHelp}
                                                />
                                            </Box>
                                            <Autocomplete
                                                renderTags={(tagValue, getTagProps) =>
                                                    tagValue.map((option, index) => (
                                                        <Chip
                                                            key={index}
                                                            label={option}
                                                            {...getTagProps({ index })}
                                                            sx={{
                                                                '& .MuiChip-deleteIcon': {
                                                                    color: 'black !important'
                                                                }
                                                            }}
                                                        />
                                                    ))
                                                }
                                                multiple
                                                options={patioAndPorchFeatures}
                                                value={values.patioAndPorchFeatures || []}
                                                onChange={(event, newValue) => setFieldValue('patioAndPorchFeatures', newValue)}
                                                onBlur={handleBlur}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        error={Boolean(touched.patioAndPorchFeatures && errors.patioAndPorchFeatures)}
                                                    />
                                                )}
                                                disableCloseOnSelect
                                                renderOption={(props, option, { selected }) => (
                                                    <li {...props}>
                                                        <Checkbox checked={selected} />
                                                        {option}
                                                    </li>
                                                )}
                                            />
                                            {touched.patioAndPorchFeatures && errors.patioAndPorchFeatures && (
                                                <FormHelperText>{errors.patioAndPorchFeatures}</FormHelperText>
                                            )}
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sm={4}>
                                        <FormControl fullWidth error={touched.spaFeatures && Boolean(errors.spaFeatures)}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <FormLabel sx={{ mb: 1, '& .MuiFormLabel-asterisk': { color: 'red' } }}>
                                                    <Typography variant="subtitle1" fontWeight="bold" component="span">
                                                        Spa Features
                                                    </Typography>
                                                </FormLabel>
                                                <HelpIcon
                                                    title={tips?.ExteriorFeatures.title}
                                                    ruleHelp={tips?.ExteriorFeatures.ruleHelp}
                                                    generalHelpDesc={tips?.ExteriorFeatures.generalHelpDesc}
                                                    generalHelp={tips?.ExteriorFeatures.generalHelp}
                                                />
                                            </Box>
                                            <Autocomplete
                                                renderTags={(tagValue, getTagProps) =>
                                                    tagValue.map((option, index) => (
                                                        <Chip
                                                            key={index}
                                                            label={option}
                                                            {...getTagProps({ index })}
                                                            sx={{
                                                                '& .MuiChip-deleteIcon': {
                                                                    color: 'black !important'
                                                                }
                                                            }}
                                                        />
                                                    ))
                                                }
                                                multiple
                                                options={spaFeatures}
                                                value={values.spaFeatures || []}
                                                onChange={(event, newValue) => setFieldValue('spaFeatures', newValue)}
                                                onBlur={handleBlur}
                                                renderInput={(params) => (
                                                    <TextField {...params} error={Boolean(touched.spaFeatures && errors.spaFeatures)} />
                                                )}
                                                disableCloseOnSelect
                                                renderOption={(props, option, { selected }) => (
                                                    <li {...props}>
                                                        <Checkbox checked={selected} />
                                                        {option}
                                                    </li>
                                                )}
                                            />
                                            {touched.spaFeatures && errors.spaFeatures && (
                                                <FormHelperText>{errors.spaFeatures}</FormHelperText>
                                            )}
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sm={4}>
                                        <FormControl fullWidth error={touched.horseAmenities && Boolean(errors.horseAmenities)}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <FormLabel sx={{ mb: 1, '& .MuiFormLabel-asterisk': { color: 'red' } }}>
                                                    <Typography variant="subtitle1" fontWeight="bold" component="span">
                                                        Horse Amenities
                                                    </Typography>
                                                </FormLabel>
                                                <HelpIcon
                                                    title={tips?.HorseAmenities.title}
                                                    ruleHelp={tips?.HorseAmenities.ruleHelp}
                                                    generalHelpDesc={tips?.HorseAmenities.generalHelpDesc}
                                                    generalHelp={tips?.HorseAmenities.generalHelp}
                                                />
                                            </Box>
                                            <Autocomplete
                                                renderTags={(tagValue, getTagProps) =>
                                                    tagValue.map((option, index) => (
                                                        <Chip
                                                            key={index}
                                                            label={option}
                                                            {...getTagProps({ index })}
                                                            sx={{
                                                                '& .MuiChip-deleteIcon': {
                                                                    color: 'black !important'
                                                                }
                                                            }}
                                                        />
                                                    ))
                                                }
                                                multiple
                                                options={horseAmenities}
                                                value={values.horseAmenities || []}
                                                onChange={(event, newValue) => setFieldValue('horseAmenities', newValue)}
                                                onBlur={handleBlur}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        error={Boolean(touched.horseAmenities && errors.horseAmenities)}
                                                    />
                                                )}
                                                disableCloseOnSelect
                                                renderOption={(props, option, { selected }) => (
                                                    <li {...props}>
                                                        <Checkbox checked={selected} />
                                                        {option}
                                                    </li>
                                                )}
                                            />
                                            {touched.horseAmenities && errors.horseAmenities && (
                                                <FormHelperText>{errors.horseAmenities}</FormHelperText>
                                            )}
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sm={4}>
                                        <FormControl fullWidth error={touched.appliancesMaintained && Boolean(errors.appliancesMaintained)}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <FormLabel sx={{ mb: 1, '& .MuiFormLabel-asterisk': { color: 'red' } }}>
                                                    <Typography variant="subtitle1" fontWeight="bold" component="span">
                                                        Appliances Maintained
                                                    </Typography>
                                                </FormLabel>
                                                <HelpIcon
                                                    title={tips?.AppliancesMaintained.title}
                                                    ruleHelp={tips?.AppliancesMaintained.ruleHelp}
                                                    generalHelpDesc={tips?.AppliancesMaintained.generalHelpDesc}
                                                    generalHelp={tips?.AppliancesMaintained.generalHelp}
                                                />
                                            </Box>
                                            <Autocomplete
                                                renderTags={(tagValue, getTagProps) =>
                                                    tagValue.map((option, index) => (
                                                        <Chip
                                                            key={index}
                                                            label={option}
                                                            {...getTagProps({ index })}
                                                            sx={{
                                                                '& .MuiChip-deleteIcon': {
                                                                    color: 'black !important'
                                                                }
                                                            }}
                                                        />
                                                    ))
                                                }
                                                multiple
                                                options={appliancesMaintained}
                                                value={values.appliancesMaintained}
                                                onChange={(event, newValue) => setFieldValue('appliancesMaintained', newValue)}
                                                onBlur={handleBlur}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        error={touched.appliancesMaintained && Boolean(errors.appliancesMaintained)}
                                                        variant="outlined"
                                                        placeholder="Select Appliances"
                                                    />
                                                )}
                                                disableCloseOnSelect
                                                renderOption={(props, option, { selected }) => (
                                                    <li {...props}>
                                                        <Checkbox checked={selected} />
                                                        {option}
                                                    </li>
                                                )}
                                            />
                                            {touched.appliancesMaintained && errors.appliancesMaintained && (
                                                <FormHelperText>{errors.appliancesMaintained}</FormHelperText>
                                            )}
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sm={4}>
                                        <FormControl fullWidth error={touched.fencing && Boolean(errors.fencing)}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <FormLabel sx={{ mb: 1, '& .MuiFormLabel-asterisk': { color: 'red' } }}>
                                                    <Typography variant="subtitle1" fontWeight="bold" component="span">
                                                        Fencing
                                                    </Typography>
                                                </FormLabel>
                                                <HelpIcon
                                                    title={tips?.Fencing.title}
                                                    ruleHelp={tips?.Fencing.ruleHelp}
                                                    generalHelpDesc={tips?.Fencing.generalHelpDesc}
                                                    generalHelp={tips?.Fencing.generalHelp}
                                                />
                                            </Box>
                                            <Autocomplete
                                                renderTags={(tagValue, getTagProps) =>
                                                    tagValue.map((option, index) => (
                                                        <Chip
                                                            key={index}
                                                            label={option}
                                                            {...getTagProps({ index })}
                                                            sx={{
                                                                '& .MuiChip-deleteIcon': {
                                                                    color: 'black !important'
                                                                }
                                                            }}
                                                        />
                                                    ))
                                                }
                                                multiple
                                                options={fencing}
                                                value={values.fencing}
                                                onChange={(event, newValue) => setFieldValue('fencing', newValue)}
                                                onBlur={handleBlur}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        error={touched.fencing && Boolean(errors.fencing)}
                                                        variant="outlined"
                                                        placeholder="Select Fencing Options"
                                                    />
                                                )}
                                                disableCloseOnSelect
                                                renderOption={(props, option, { selected }) => (
                                                    <li {...props}>
                                                        <Checkbox checked={selected} />
                                                        {option}
                                                    </li>
                                                )}
                                            />
                                            {touched.fencing && errors.fencing && <FormHelperText>{errors.fencing}</FormHelperText>}
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sm={4}>
                                        <FormControl fullWidth error={touched.fireplaceFeatures && Boolean(errors.fireplaceFeatures)}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <FormLabel sx={{ mb: 1 }}>
                                                    <Typography variant="subtitle1" fontWeight="bold" component="span">
                                                        Fireplace Features
                                                    </Typography>
                                                </FormLabel>
                                                <HelpIcon
                                                    title={tips?.fireplaceFeatures.title}
                                                    ruleHelp={tips?.fireplaceFeatures.ruleHelp}
                                                    generalHelpDesc={tips?.fireplaceFeatures.generalHelpDesc}
                                                    generalHelp={tips?.fireplaceFeatures.generalHelp}
                                                />
                                            </Box>
                                            <Autocomplete
                                                renderTags={(tagValue, getTagProps) =>
                                                    tagValue.map((option, index) => (
                                                        <Chip
                                                            key={index}
                                                            label={option}
                                                            {...getTagProps({ index })}
                                                            sx={{
                                                                '& .MuiChip-deleteIcon': {
                                                                    color: 'black !important'
                                                                }
                                                            }}
                                                        />
                                                    ))
                                                }
                                                multiple
                                                options={fireplaceFeatures}
                                                value={values.fireplaceFeatures}
                                                onChange={(event, newValue) => setFieldValue('fireplaceFeatures', newValue)}
                                                onBlur={handleBlur}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        error={touched.fireplaceFeatures && Boolean(errors.fireplaceFeatures)}
                                                        variant="outlined"
                                                        placeholder="Select Fireplace Features"
                                                    />
                                                )}
                                                disableCloseOnSelect
                                                renderOption={(props, option, { selected }) => (
                                                    <li {...props}>
                                                        <Checkbox checked={selected} />
                                                        {option}
                                                    </li>
                                                )}
                                            />
                                            {touched.fireplaceFeatures && errors.fireplaceFeatures && (
                                                <FormHelperText>{errors.fireplaceFeatures}</FormHelperText>
                                            )}
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sm={4}>
                                        <FormControl fullWidth>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <FormLabel sx={{ mb: 1 }}>
                                                    <Typography variant="subtitle1" fontWeight="bold" component="span">
                                                        No. of Fireplaces
                                                    </Typography>
                                                </FormLabel>
                                                <HelpIcon
                                                    title={tips?.Fireplaces.title}
                                                    ruleHelp={tips?.Fireplaces.ruleHelp}
                                                    generalHelpDesc={tips?.Fireplaces.generalHelpDesc}
                                                    generalHelp={tips?.Fireplaces.generalHelp}
                                                />
                                            </Box>

                                            <TextField
                                                name="fireplaces"
                                                value={values.fireplaces}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={touched.fireplaces && Boolean(errors.fireplaces)}
                                                helperText={touched.fireplaces && errors.fireplaces}
                                            />
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sm={4}>
                                        <FormControl fullWidth error={touched.otherStructures && Boolean(errors.otherStructures)}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <FormLabel sx={{ mb: 1 }}>
                                                    <Typography variant="subtitle1" fontWeight="bold" component="span">
                                                        Other Structures
                                                    </Typography>
                                                </FormLabel>
                                                <HelpIcon
                                                    title={tips?.otherStructures.title}
                                                    ruleHelp={tips?.otherStructures.ruleHelp}
                                                    generalHelpDesc={tips?.otherStructures.generalHelpDesc}
                                                    generalHelp={tips?.otherStructures.generalHelp}
                                                />
                                            </Box>

                                            <Autocomplete
                                                renderTags={(tagValue, getTagProps) =>
                                                    tagValue.map((option, index) => (
                                                        <Chip
                                                            key={index}
                                                            label={option}
                                                            {...getTagProps({ index })}
                                                            sx={{
                                                                '& .MuiChip-deleteIcon': {
                                                                    color: 'black !important'
                                                                }
                                                            }}
                                                        />
                                                    ))
                                                }
                                                multiple
                                                options={otherStructures}
                                                value={values.otherStructures}
                                                onChange={(event, newValue) => setFieldValue('otherStructures', newValue)}
                                                onBlur={handleBlur}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        error={touched.otherStructures && Boolean(errors.otherStructures)}
                                                        variant="outlined"
                                                        placeholder="Select Other Structures"
                                                    />
                                                )}
                                                disableCloseOnSelect
                                                renderOption={(props, option, { selected }) => (
                                                    <li {...props}>
                                                        <Checkbox checked={selected} />
                                                        {option}
                                                    </li>
                                                )}
                                            />
                                            {touched.otherStructures && errors.otherStructures && (
                                                <FormHelperText>{errors.otherStructures}</FormHelperText>
                                            )}
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sm={4}>
                                        <FormControl fullWidth>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <FormLabel sx={{ mb: 1 }}>
                                                    <Typography variant="subtitle1" fontWeight="bold" component="span">
                                                        Community Web Site
                                                    </Typography>
                                                </FormLabel>
                                                <HelpIcon
                                                    title={tips?.communityWebSite.title}
                                                    ruleHelp={tips?.communityWebSite.ruleHelp}
                                                    generalHelpDesc={tips?.communityWebSite.generalHelpDesc}
                                                />
                                            </Box>
                                            <Field
                                                as={TextField}
                                                fullWidth
                                                name="communityWebSite"
                                                variant="outlined"
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                value={values.communityWebSite}
                                                error={touched.communityWebSite && errors.communityWebSite}
                                                helperText={<ErrorMessage name="communityWebSite" />}
                                            />
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sm={6} />

                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth error={touched.guestAccommodations && Boolean(errors.guestAccommodations)}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <FormLabel sx={{ mb: 1, '& .MuiFormLabel-asterisk': { color: 'red' } }}>
                                                    <Typography variant="subtitle1" fontWeight="bold" component="span">
                                                        Guest Accommodations
                                                    </Typography>
                                                </FormLabel>
                                                <HelpIcon
                                                    title={tips?.guestAccommodations.title}
                                                    ruleHelp={tips?.guestAccommodations.ruleHelp}
                                                    generalHelpDesc={tips?.guestAccommodations.generalHelpDesc}
                                                />
                                            </Box>

                                            <Autocomplete
                                                renderTags={(tagValue, getTagProps) =>
                                                    tagValue.map((option, index) => (
                                                        <Chip
                                                            key={index}
                                                            label={option}
                                                            {...getTagProps({ index })}
                                                            sx={{
                                                                '& .MuiChip-deleteIcon': {
                                                                    color: 'black !important'
                                                                }
                                                            }}
                                                        />
                                                    ))
                                                }
                                                multiple
                                                options={guestAccommodations}
                                                value={values.guestAccommodations}
                                                onChange={(event, newValue) => setFieldValue('guestAccommodations', newValue)}
                                                onBlur={handleBlur}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        error={touched.guestAccommodations && Boolean(errors.guestAccommodations)}
                                                        variant="outlined"
                                                        placeholder="Select Guest Accommodations"
                                                    />
                                                )}
                                                disableCloseOnSelect
                                                renderOption={(props, option, { selected }) => (
                                                    <li {...props}>
                                                        <Checkbox checked={selected} />
                                                        {option}
                                                    </li>
                                                )}
                                            />

                                            {touched.guestAccommodations && errors.guestAccommodations && (
                                                <FormHelperText>{errors.guestAccommodations}</FormHelperText>
                                            )}
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sm={6} />

                                    <Grid item xs={12} sm={4}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <FormLabel sx={{ '& .MuiFormLabel-asterisk': { color: 'red' } }}>
                                                <Typography variant="subtitle1" fontWeight="bold" component="span">
                                                    Guest Beds
                                                </Typography>
                                            </FormLabel>
                                            <HelpIcon
                                                title={tips?.guestBeds.title}
                                                ruleHelp={tips?.guestBeds.ruleHelp}
                                                generalHelpDesc={tips?.guestBeds.generalHelpDesc}
                                                // generalHelp={tips?.guestBeds.generalHelp}
                                            />
                                        </Box>
                                        <TextField
                                            fullWidth
                                            name="guestBeds"
                                            type="number"
                                            value={values.guestBeds}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        />

                                        {touched.guestBeds && errors.guestBeds && <FormHelperText error>{errors.guestBeds}</FormHelperText>}
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <FormLabel sx={{ '& .MuiFormLabel-asterisk': { color: 'red' } }}>
                                                <Typography variant="subtitle1" fontWeight="bold" component="span">
                                                    Guest Full Baths
                                                </Typography>
                                            </FormLabel>
                                            <HelpIcon
                                                title={tips?.guestFullBaths.title}
                                                ruleHelp={tips?.guestFullBaths.ruleHelp}
                                                generalHelpDesc={tips?.guestFullBaths.generalHelpDesc}
                                                // generalHelp={tips?.guestFullBaths.generalHelp}
                                            />
                                        </Box>
                                        <TextField
                                            fullWidth
                                            name="guestFullBaths"
                                            value={values.guestFullBaths}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        />

                                        {touched.guestFullBaths && errors.guestFullBaths && (
                                            <FormHelperText error>{errors.guestFullBaths}</FormHelperText>
                                        )}
                                    </Grid>

                                    <Grid item xs={12} sm={4}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <FormLabel sx={{ '& .MuiFormLabel-asterisk': { color: 'red' } }}>
                                                <Typography variant="subtitle1" fontWeight="bold" component="span">
                                                    Guest Half Baths
                                                </Typography>
                                            </FormLabel>
                                            <HelpIcon
                                                title={tips?.guestHalfBaths.title}
                                                ruleHelp={tips?.guestHalfBaths.ruleHelp}
                                                generalHelpDesc={tips?.guestHalfBaths.generalHelpDesc}
                                                // generalHelp={tips?.guestFullBaths.generalHelp}
                                            />
                                        </Box>
                                        <TextField
                                            fullWidth
                                            name="guestHalfBaths"
                                            value={values.guestHalfBaths}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        />

                                        {touched.guestHalfBaths && errors.guestHalfBaths && (
                                            <FormHelperText error>{errors.guestHalfBaths}</FormHelperText>
                                        )}
                                    </Grid>

                                    <Grid item xs={12} sm={12}>
                                        <FormControl fullWidth error={touched.furnished && Boolean(errors.furnished)}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <FormLabel sx={{ mb: 1, '& .MuiFormLabel-asterisk': { color: 'red' } }}>
                                                    <Typography variant="subtitle1" fontWeight="bold" component="span">
                                                        Is Property Furnished ?
                                                    </Typography>
                                                </FormLabel>
                                                <HelpIcon
                                                    title={tips?.Furnished.title}
                                                    ruleHelp={tips?.Furnished.ruleHelp}
                                                    generalHelpDesc={tips?.Furnished.generalHelpDesc}
                                                    generalHelp={tips?.Furnished.generalHelp}
                                                />
                                            </Box>

                                            <RadioGroup
                                                aria-label="furnished"
                                                name="furnished"
                                                value={values.furnished}
                                                onChange={handleChange}
                                                row
                                                sx={{ gap: 1, ml: 1, mt: 1 }}
                                            >
                                                {furnished.map((option) => (
                                                    <FormControlLabel
                                                        key={option}
                                                        value={option}
                                                        control={<Radio />}
                                                        label={option}
                                                        sx={{
                                                            border: '1px solid #ccc',
                                                            borderRadius: '5px',
                                                            padding: '2px 14px',
                                                            minWidth: '180px'
                                                        }}
                                                    />
                                                ))}
                                            </RadioGroup>
                                            {/* <Select
                                                value={values.}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                input={<OutlinedInput />}
                                              
                                            >
                                                {.map((option) => (
                                                    <MenuItem key={option} value={option} sx={{ my: 0.5 }}>
                                                        <ListItemText primary={option} />
                                                    </MenuItem>
                                                ))}
                                            </Select> */}
                                            {touched.furnished && errors.furnished && <FormHelperText>{errors.furnished}</FormHelperText>}
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sm={12}>
                                        <FormControl fullWidth error={touched.distanceToUTShuttle && Boolean(errors.distanceToUTShuttle)}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <FormLabel sx={{ '& .MuiFormLabel-asterisk': { color: 'red' } }}>
                                                    <Typography variant="subtitle1" fontWeight="bold" component="span">
                                                        Distance to UT Shuttle
                                                    </Typography>
                                                </FormLabel>
                                                <HelpIcon
                                                    title={tips?.distanceToUTShuttle.title}
                                                    ruleHelp={tips?.distanceToUTShuttle.ruleHelp}
                                                    generalHelpDesc={tips?.distanceToUTShuttle.generalHelpDesc}
                                                    // generalHelp={tips?.guestFullBaths.generalHelp}
                                                />
                                            </Box>

                                            <RadioGroup
                                                aria-label="distanceToUTShuttle"
                                                name="distanceToUTShuttle"
                                                value={values.distanceToUTShuttle}
                                                onChange={handleChange}
                                                row
                                                sx={{ gap: 1, ml: 1, mt: 1 }}
                                            >
                                                {distances.map((option) => (
                                                    <FormControlLabel
                                                        key={option}
                                                        value={option}
                                                        control={<Radio />}
                                                        label={option}
                                                        sx={{
                                                            border: '1px solid #ccc',
                                                            borderRadius: '5px',
                                                            padding: '2px 14px',
                                                            minWidth: '180px'
                                                        }}
                                                    />
                                                ))}
                                            </RadioGroup>

                                            {/* <Select
                                                    id=""
                                                    name="distanceToUTShuttle"
                                                    label="Distance to UT Shuttle"
                                                    value={values.distanceToUTShuttle}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    input={<OutlinedInput label="Distance to UT Shuttle" />}
                                                >
                                                    {.map((option) => (
                                                        <MenuItem key={option} value={option} sx={{ my: 0.5 }}>
                                                            <ListItemText primary={option} />
                                                        </MenuItem>
                                                    ))}
                                                </Select> */}
                                            {touched.distanceToUTShuttle && errors.distanceToUTShuttle && (
                                                <FormHelperText>{errors.distanceToUTShuttle}</FormHelperText>
                                            )}
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sm={12}>
                                        <FormControl fullWidth error={touched.distanceToMetro && Boolean(errors.distanceToMetro)}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <FormLabel sx={{ '& .MuiFormLabel-asterisk': { color: 'red' } }}>
                                                    <Typography variant="subtitle1" fontWeight="bold" component="span">
                                                        Distance to Metro
                                                    </Typography>
                                                </FormLabel>

                                                <HelpIcon
                                                    title={tips?.distanceToMetro.title}
                                                    ruleHelp={tips?.distanceToMetro.ruleHelp}
                                                    generalHelpDesc={tips?.distanceToMetro.generalHelpDesc}
                                                    // generalHelp={tips?.guestFullBaths.generalHelp}
                                                />
                                            </Box>

                                            <RadioGroup
                                                aria-label="distanceToMetro"
                                                name="distanceToMetro"
                                                value={values.distanceToMetro}
                                                onChange={handleChange}
                                                row
                                                sx={{ gap: 1, ml: 1, mt: 1 }}
                                            >
                                                {distances.map((option) => (
                                                    <FormControlLabel
                                                        key={option}
                                                        value={option}
                                                        control={<Radio />}
                                                        label={option}
                                                        sx={{
                                                            border: '1px solid #ccc',
                                                            borderRadius: '5px',
                                                            padding: '2px 14px',
                                                            minWidth: '180px'
                                                        }}
                                                    />
                                                ))}
                                            </RadioGroup>

                                            {/* <Select
                                                    id=""
                                                    name="distanceToMetro"
                                                    label="Distance to Metro"
                                                    value={values.distanceToMetro}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    input={<OutlinedInput label="Distance to Metro" />}
                                                >
                                                    {.map((option) => (
                                                        <MenuItem key={option} value={option} sx={{ my: 0.5 }}>
                                                            <ListItemText primary={option} />
                                                        </MenuItem>
                                                    ))}
                                                </Select> */}
                                            {touched.distanceToMetro && errors.distanceToMetro && (
                                                <FormHelperText>{errors.distanceToMetro}</FormHelperText>
                                            )}
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sm={12}>
                                        <FormControl fullWidth error={touched.distanceToLightRail && Boolean(errors.distanceToLightRail)}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <FormLabel sx={{ '& .MuiFormLabel-asterisk': { color: 'red' } }}>
                                                    <Typography variant="subtitle1" fontWeight="bold" component="span">
                                                        Distance to Light Rail
                                                    </Typography>
                                                </FormLabel>

                                                <HelpIcon
                                                    title={tips?.distanceToLightRail.title}
                                                    ruleHelp={tips?.distanceToLightRail.ruleHelp}
                                                    generalHelpDesc={tips?.distanceToLightRail.generalHelpDesc}
                                                    // generalHelp={tips?.guestFullBaths.generalHelp}
                                                />
                                            </Box>

                                            <RadioGroup
                                                aria-label="distanceToLightRail"
                                                name="distanceToLightRail"
                                                value={values.distanceToLightRail}
                                                onChange={handleChange}
                                                row
                                                sx={{ gap: 1, ml: 1, mt: 1 }}
                                            >
                                                {distances.map((option) => (
                                                    <FormControlLabel
                                                        key={option}
                                                        value={option}
                                                        control={<Radio />}
                                                        label={option}
                                                        sx={{
                                                            border: '1px solid #ccc',
                                                            borderRadius: '5px',
                                                            padding: '2px 14px',
                                                            minWidth: '180px'
                                                        }}
                                                    />
                                                ))}
                                            </RadioGroup>

                                            {/* <Select
                                                    id=""
                                                    name="distanceToLightRail"
                                                    label="Distance to Light Rail"
                                                    value={values.distanceToLightRail}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    input={<OutlinedInput label="Distance to Light Rail" />}
                                                >
                                                    {.map((option) => (
                                                        <MenuItem key={option} value={option} sx={{ my: 0.5 }}>
                                                            <ListItemText primary={option} />
                                                        </MenuItem>
                                                    ))}
                                                </Select> */}
                                            {touched.distanceToLightRail && errors.distanceToLightRail && (
                                                <FormHelperText>{errors.distanceToLightRail}</FormHelperText>
                                            )}
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </SubCard>
                        </Collapse>
                    </Grid>
                </Grid>

                {errors.submit && (
                    <Grid item xs={12}>
                        <FormHelperText error>{errors.submit}</FormHelperText>
                    </Grid>
                )}
            </Grid>
        </Grid>
    );
};

export default AdditionalInfo;
